import React, { createContext, useState, useEffect, useCallback } from "react"; // 👈 CHANGED: added useCallback
import jwt_decode from "jwt-decode";
import { pick } from "lodash";
import { useNavigate } from "react-router-dom";
import useToastContext from "hooks/useToastContext";
import { apiPost, apiPut, apiGet } from "../utils/apiFetch";
import apiPath from "../utils/pathObj";

const AuthContext = createContext();

export default AuthContext;

export const AuthProvider = ({ children }) => {
  const safeJsonParseTopLevel = (str, defaultValue = null) => {
    try {
      if (!str || str === "undefined" || str === "null") return defaultValue;
      return JSON.parse(str);
    } catch (error) {
      console.error("JSON parse error:", error);
      return defaultValue;
    }
  };

  let [user, setUser] = useState(() => {
    const token = sessionStorage.getItem("token");
    const storedUser = sessionStorage.getItem("userData");
    if (!token) return null;

    // Check for stored user data first (complete user object)
    if (storedUser) {
      const parsed = safeJsonParseTopLevel(storedUser);
      if (parsed) return parsed;
    }

    // Fallback to token decode (basic info only)
    return jwt_decode(token);
  });

  const [sidebarStatus, setSidebarStatus] = useState("");
  const [pageName, setPageName] = useState(
    sessionStorage.getItem("pageName")
      ? sessionStorage.getItem("pageName")
      : "Dashboard",
  );
  const navigate = useNavigate();
  const notification = useToastContext();
  const [loginMessage, setLoginMessage] = useState(
    "Admin details updated successfully.",
  );

  // Add company completion status state
  const [companyCompletionStatus, setCompanyCompletionStatus] = useState({
    percentage: 0,
    missingFields: [],
    isProfileComplete: false,
    hrCount: 0,
    accountantCount: 0,
    message: "",
  });
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [isLoadingCompanyStatus, setIsLoadingCompanyStatus] = useState(false);

  // 👇 CHANGED: Pipedrive connection state shared across the app
  const [pipedriveConnected, setPipedriveConnected] = useState(false);
  const [pipedriveStatusLoading, setPipedriveStatusLoading] = useState(true);

  // 👇 CHANGED: fetch Pipedrive status (safe: falls back to false on any error)
  const refreshPipedriveStatus = useCallback(async () => {
    try {
      const r = await apiGet(apiPath.pipedriveStatus, {}, { skipLoader: true });
      if (r?.status === 200 && r?.data?.success) {
        setPipedriveConnected(!!r.data.data?.connected);
      } else {
        setPipedriveConnected(false);
      }
    } catch {
      setPipedriveConnected(false); // 401 / 403 / 500 → treat as not connected
    } finally {
      setPipedriveStatusLoading(false);
    }
  }, []);

  // Safe JSON parse function
  const safeJsonParse = (str, defaultValue = null) => {
    try {
      if (!str || str === "undefined" || str === "null") {
        return defaultValue;
      }
      return JSON.parse(str);
    } catch (error) {
      console.error("JSON parse error:", error);
      return defaultValue;
    }
  };

  // Function to fetch company completion status
  const fetchCompanyCompletionStatus = async () => {
    try {
      setIsLoadingCompanyStatus(true);
      const result = await apiGet(apiPath.companyCompletionStatus); // Make sure this path is defined in your pathObj

      if (result?.data?.success) {
        const completionData = result.data.results;

        if (completionData) {
          setCompanyCompletionStatus(completionData);
          // if (completionData?.message !== "Your company profile is completed!"){
          //   setTimeout(() => {notification.success(completionData?.message)}, 10000)
          // }
          // Store in sessionStorage for persistence
          sessionStorage.setItem(
            "companyCompletionStatus",
            JSON.stringify(completionData),
          );
          return completionData;
        } else {
          console.warn("No completion data in response");
          return null;
        }
      } else {
        throw new Error(
          result?.data?.message || "Failed to fetch company status",
        );
      }
    } catch (error) {
      console.error("Error fetching company completion status:", error);
      // If API fails, try to get from sessionStorage safely
      const stored = safeJsonParse(
        sessionStorage.getItem("companyCompletionStatus"),
      );
      if (stored) {
        setCompanyCompletionStatus(stored);
      }
      return null;
    } finally {
      setIsLoadingCompanyStatus(false);
    }
  };

  // Function to refresh company completion status (call this after company updates)
  const refreshCompanyCompletionStatus = async () => {
    return await fetchCompanyCompletionStatus();
  };

  const updatePageName = (name) => {
    sessionStorage.setItem("pageName", name);
    setPageName(name);
  };

  let loginUser = async (body) => {
    document.getElementById("loader").classList.remove("hidden");
    try {
    const { status, data } = await apiPost(
      apiPath.loginUser,
      pick(body, [
        "email",
        "password",
        "deviceId",
        "deviceType",
        "deviceToken",
        "latitude",
        "longitude",
        "loginAddress",
        "loginIp",
        "fraudDeviceId"
      ]),
    );

    if (status === 200) {
      if (data.success) {
        // === MFA branch: no token yet, just a challenge to complete ===
        if (data?.results?.mfaRequired || data?.results?.mfaSetupRequired) {
          setMfaChallenge({
            setup: !!data.results.mfaSetupRequired,
            preAuthToken: data.results.preAuthToken,
            userId: data.results.userId,
            role: data.results.role,
            qrCodeDataUrl: data.results.qrCodeDataUrl,
            manualEntryKey: data.results.manualEntryKey,
          });
          document.getElementById("loader").classList.add("hidden");
          return { mfaRequired: true };
        }
        // === END MFA branch ===

        const token = data?.results?.token || null;
        const refresh_token = data?.results?.refresh_token || null;

        // Store full user data
        const userData = data?.results;
        const {
          token: _token,
          refresh_token: _refresh,
          ...userInfo
        } = userData;

        sessionStorage.setItem("token", token);
        sessionStorage.setItem("refresh_token", refresh_token);
        sessionStorage.setItem("sidebar", "open");
        setSidebarStatus("open");
        sessionStorage.setItem("pageName", "Dashboard");
        setPageName("Dashboard");

        // Set full user data
        setUser(userInfo);
        sessionStorage.setItem("userData", JSON.stringify(userInfo));

        fetchCompanyCompletionStatus()
          .catch((error) => {
            console.error("Failed to fetch company completion:", error);
          });

        // 👇 CHANGED: load Pipedrive status once the user is logged in
        refreshPipedriveStatus();

        navigate("/dashboard");
        notification.success(data.message);
      } else {
        notification.error(
          data?.message ||
            "Login is not allowed from this IP. Sign in from a company-approved office/network, or ask HR to add this IP for work from home.",
        );
      }
    }
    } catch (err) {
      const data = err?.response?.data;
      notification.error(
        data?.message ||
          "Login is not allowed from this IP. Sign in from a company-approved office/network, or ask HR to add this IP for work from home.",
      );
    } finally {
      const el = document.getElementById("loader");
      if (el) el.classList.add("hidden");
    }
  };

  // New: completes login after the user enters their TOTP/recovery code
  let completeMfaLogin = async (code, { isRecoveryCode } = {}) => {
    if (!mfaChallenge?.preAuthToken) {
      notification.error(
        "Your login session has expired. Please sign in again.",
      );
      return { verified: false };
    }

    document.getElementById("loader").classList.remove("hidden");
    try {
      let loginGps = {};
      try {
        loginGps = JSON.parse(sessionStorage.getItem("loginGps") || "{}") || {};
      } catch (err) {
        loginGps = {};
      }
      const body = isRecoveryCode
        ? {
            preAuthToken: mfaChallenge.preAuthToken,
            recoveryCode: code,
            role: mfaChallenge.role,
            ...loginGps,
          }
        : {
            preAuthToken: mfaChallenge.preAuthToken,
            token: code,
            role: mfaChallenge.role,
            ...loginGps,
          };

      const { data } = await apiPost(apiPath.mfaLoginVerify, body);

      if (data?.success) {
        const token = data?.results?.token || null;
        const refresh_token = data?.results?.refresh_token || null;

        // Store the full user data from the response
        const userData = data?.results;

        // Remove token-related fields from userData to keep it clean
        const {
          token: _token,
          refresh_token: _refresh,
          ...userInfo
        } = userData;

        sessionStorage.setItem("token", token);
        sessionStorage.setItem("refresh_token", refresh_token);
        sessionStorage.setItem("sidebar", "open");
        setSidebarStatus("open");
        sessionStorage.setItem("pageName", "Dashboard");
        setPageName("Dashboard");

        // Set the full user data instead of just the decoded token
        setUser(userInfo);

        // Also store user data in sessionStorage for persistence
        sessionStorage.setItem("userData", JSON.stringify(userInfo));

        setMfaChallenge(null);

        fetchCompanyCompletionStatus().catch((err) =>
          console.error("Failed to fetch company completion:", err),
        );

        // 👇 CHANGED: load Pipedrive status after MFA login
        refreshPipedriveStatus();

        navigate("/dashboard");
        notification.success(data.message || "Login successful.");
        return { verified: true };
      }

      // Backend issues a fresh preAuthToken on failed attempts — keep it current
      if (data?.data?.preAuthToken) {
        setMfaChallenge((prev) => ({
          ...prev,
          preAuthToken: data.data.preAuthToken,
        }));
      }
      return { verified: false, message: data?.message };
    } catch (err) {
      return {
        verified: false,
        message:
          err?.response?.data?.message ||
          "Verification failed. Please try again.",
      };
    } finally {
      document.getElementById("loader").classList.add("hidden");
    }
  };

  let completeMfaSetupLogin = async (code) => {
    if (!mfaChallenge?.preAuthToken) {
      notification.error(
        "Your login session has expired. Please sign in again.",
      );
      return { verified: false };
    }
    try {
      let loginGps = {};
      try {
        loginGps = JSON.parse(sessionStorage.getItem("loginGps") || "{}") || {};
      } catch (err) {
        loginGps = {};
      }
      const { data } = await apiPost(apiPath.mfaLoginSetupVerify, {
        preAuthToken: mfaChallenge.preAuthToken,
        token: code,
        role: mfaChallenge.role,
        ...loginGps,
      });
      if (data?.success) {
        setMfaChallenge((prev) => ({
          ...prev,
          pendingLogin: data,
          recoveryCodes: data?.results?.recoveryCodes || [],
        }));
        return {
          verified: true,
          recoveryCodes: data?.results?.recoveryCodes || [],
        };
      }
      if (data?.data?.preAuthToken) {
        setMfaChallenge((prev) => ({
          ...prev,
          preAuthToken: data.data.preAuthToken,
        }));
      }
      return { verified: false, message: data?.message };
    } catch (err) {
      const payload = err?.response?.data;
      if (payload?.data?.preAuthToken) {
        setMfaChallenge((prev) => ({
          ...prev,
          preAuthToken: payload.data.preAuthToken,
        }));
      }
      return {
        verified: false,
        message: payload?.message || "Verification failed. Please try again.",
      };
    }
  };

  let finishMfaSetupLogin = async () => {
    const data = mfaChallenge?.pendingLogin;
    setMfaChallenge(null);
    if (!data?.success) return;
    const token = data?.results?.token || null;
    const refresh_token = data?.results?.refresh_token || null;
    const userData = data?.results || {};
    const {
      token: _token,
      refresh_token: _refresh,
      recoveryCodes: _rc,
      ...userInfo
    } = userData;
    sessionStorage.setItem("token", token);
    sessionStorage.setItem("refresh_token", refresh_token);
    sessionStorage.setItem("sidebar", "open");
    setSidebarStatus("open");
    sessionStorage.setItem("pageName", "Dashboard");
    setPageName("Dashboard");
    setUser(userInfo);
    sessionStorage.setItem("userData", JSON.stringify(userInfo));
    fetchCompanyCompletionStatus().catch((err) =>
      console.error("Failed to fetch company completion:", err),
    );

    // 👇 CHANGED: load Pipedrive status after MFA setup login
    refreshPipedriveStatus();

    navigate("/dashboard");
    notification.success(data.message || "Login successful.");
  };

  let cancelMfaChallenge = () => setMfaChallenge(null);

  const updateProfile = async (formData, setPicture, setLogo) => {
    try {
      const res = await apiPut(apiPath.editProfile, formData);
      console.log(res);
      const token = res?.data?.results?.token || null;
      const refresh_token = res?.data?.results?.refresh_token || null;
      sessionStorage.setItem("token", token);
      setLoginMessage(res?.data?.message);
      sessionStorage.setItem("refresh_token", refresh_token);
      setUser(jwt_decode(token));
      const { message, success } = res.data;
      if (res && success) {
        notification.success(message);
        setPicture({
          cropperOpen: false,
          img: null,
          zoom: 2,
          croppedImg: res?.data?.results?.profilePicture,
        });
        setLogo({
          cropperOpen: false,
          img: null,
          zoom: 2,
          croppedImg: res?.data?.results?.logo,
        });

        // Refresh company completion status after profile update
        refreshCompanyCompletionStatus();
      }
    } catch (err) {
      console.log("err:", err);
    }
  };

  const handleSidebar = () => {
    let s1 = sessionStorage.getItem("sidebar");

    sessionStorage.setItem("sidebar", s1 == "open" ? "close" : "open");
    setSidebarStatus(s1 == "open" ? "close" : "open");
  };

  let logoutUser = async () => {
    try {
      await apiGet(apiPath.logout);
    } catch (err) {
      console.log("logout:", err?.message);
    }
    setUser(null);
    setCompanyCompletionStatus({
      percentage: 0,
      missingFields: [],
      isProfileComplete: false,
      hrCount: 0,
      accountantCount: 0,
      jobCount: 0,
      message: "",
    });

    // 👇 CHANGED: reset Pipedrive state on logout
    setPipedriveConnected(false);
    setPipedriveStatusLoading(false);

    sessionStorage.removeItem("token");
    sessionStorage.removeItem("refresh_token");
    sessionStorage.removeItem("companyCompletionStatus");
    navigate("/login");
  };

  useEffect(() => {
    let sidebarOpen = "";
    if (sessionStorage.getItem("sidebar")) {
      sidebarOpen = sessionStorage.getItem("sidebar");
    } else {
      sessionStorage.setItem("sidebar", "open");
      sidebarOpen = "open";
    }
    setSidebarStatus(sidebarOpen);

    // Load company completion status from sessionStorage on initial load
    const storedCompanyStatus = sessionStorage.getItem("companyCompletionStatus");
    if (storedCompanyStatus) {
      const parsedData = safeJsonParse(storedCompanyStatus);
      if (parsedData) {
        setCompanyCompletionStatus(parsedData);
      }
    }
  }, []);

  // Initialize user and fetch fresh company status if user is already logged in
  useEffect(() => {
    if (sessionStorage.getItem("token")) {
      let userData = jwt_decode(sessionStorage.getItem("token"));
      setUser(userData);

      // Fetch fresh company completion status if user is already logged in
      // Use setTimeout to avoid blocking the initial render
      setTimeout(() => {
        fetchCompanyCompletionStatus();
      }, 1000);

      // 👇 CHANGED: also load Pipedrive status on page reload when logged in
      refreshPipedriveStatus();
    } else {
      // 👇 CHANGED: not logged in → nothing to check
      setPipedriveConnected(false);
      setPipedriveStatusLoading(false);
    }
  }, [refreshPipedriveStatus]); // 👈 CHANGED: added dependency

  // 👇 CHANGED: whenever the user changes (login/logout), keep Pipedrive state in sync
  useEffect(() => {
    if (user) {
      refreshPipedriveStatus();
    } else {
      setPipedriveConnected(false);
      setPipedriveStatusLoading(false);
    }
  }, [user, refreshPipedriveStatus]);

  let contextData = {
    user,
    loginUser: loginUser,
    logoutUser: logoutUser,
    updateProfile: updateProfile,
    sidebarStatus: sidebarStatus,
    handleSidebar,
    pageName,
    updatePageName,
    loginMessage: loginMessage,
    setUser,
    // Add company completion status to context
    companyCompletionStatus,
    isLoadingCompanyStatus,
    refreshCompanyCompletionStatus,
    fetchCompanyCompletionStatus,
    mfaChallenge,
    completeMfaLogin,
    completeMfaSetupLogin,
    finishMfaSetupLogin,
    cancelMfaChallenge,

    // 👇 CHANGED: expose Pipedrive state + refresher
    pipedriveConnected,
    pipedriveStatusLoading,
    refreshPipedriveStatus,
  };
  return (
    <AuthContext.Provider value={contextData}>{children}</AuthContext.Provider>
  );
};





// import React, { createContext, useState, useEffect } from "react";
// import jwt_decode from "jwt-decode";
// import { pick } from "lodash";
// import { useNavigate } from "react-router-dom";
// import useToastContext from "hooks/useToastContext";
// import { apiPost, apiPut, apiGet } from "../utils/apiFetch";
// import apiPath from "../utils/pathObj";

// const AuthContext = createContext();

// export default AuthContext;

// export const AuthProvider = ({ children }) => {
//   const safeJsonParseTopLevel = (str, defaultValue = null) => {
//     try {
//       if (!str || str === "undefined" || str === "null") return defaultValue;
//       return JSON.parse(str);
//     } catch (error) {
//       console.error("JSON parse error:", error);
//       return defaultValue;
//     }
//   };

//   let [user, setUser] = useState(() => {
//     const token = sessionStorage.getItem("token");
//     const storedUser = sessionStorage.getItem("userData");
//     if (!token) return null;

//     // Check for stored user data first (complete user object)
//     if (storedUser) {
//       const parsed = safeJsonParseTopLevel(storedUser);
//       if (parsed) return parsed;
//     }

//     // Fallback to token decode (basic info only)
//     return jwt_decode(token);
//   });

//   const [sidebarStatus, setSidebarStatus] = useState("");
//   const [pageName, setPageName] = useState(
//     sessionStorage.getItem("pageName")
//       ? sessionStorage.getItem("pageName")
//       : "Dashboard",
//   );
//   const navigate = useNavigate();
//   const notification = useToastContext();
//   const [loginMessage, setLoginMessage] = useState(
//     "Admin details updated successfully.",
//   );

//   // Add company completion status state
//   const [companyCompletionStatus, setCompanyCompletionStatus] = useState({
//     percentage: 0,
//     missingFields: [],
//     isProfileComplete: false,
//     hrCount: 0,
//     accountantCount: 0,
//     message: "",
//   });
//   const [mfaChallenge, setMfaChallenge] = useState(null);
//   const [isLoadingCompanyStatus, setIsLoadingCompanyStatus] = useState(false);

//   // Safe JSON parse function
//   const safeJsonParse = (str, defaultValue = null) => {
//     try {
//       if (!str || str === "undefined" || str === "null") {
//         return defaultValue;
//       }
//       return JSON.parse(str);
//     } catch (error) {
//       console.error("JSON parse error:", error);
//       return defaultValue;
//     }
//   };

//   // Function to fetch company completion status
//   const fetchCompanyCompletionStatus = async () => {
//     try {
//       setIsLoadingCompanyStatus(true);
//       const result = await apiGet(apiPath.companyCompletionStatus); // Make sure this path is defined in your pathObj

//       if (result?.data?.success) {
//         const completionData = result.data.results;

//         if (completionData) {
//           setCompanyCompletionStatus(completionData);
//           // if (completionData?.message !== "Your company profile is completed!"){
//           //   setTimeout(() => {notification.success(completionData?.message)}, 10000)
//           // }
//           // Store in sessionStorage for persistence
//           sessionStorage.setItem(
//             "companyCompletionStatus",
//             JSON.stringify(completionData),
//           );
//           return completionData;
//         } else {
//           console.warn("No completion data in response");
//           return null;
//         }
//       } else {
//         throw new Error(
//           result?.data?.message || "Failed to fetch company status",
//         );
//       }
//     } catch (error) {
//       console.error("Error fetching company completion status:", error);
//       // If API fails, try to get from sessionStorage safely
//       const stored = safeJsonParse(
//         sessionStorage.getItem("companyCompletionStatus"),
//       );
//       if (stored) {
//         setCompanyCompletionStatus(stored);
//       }
//       return null;
//     } finally {
//       setIsLoadingCompanyStatus(false);
//     }
//   };

//   // Function to refresh company completion status (call this after company updates)
//   const refreshCompanyCompletionStatus = async () => {
//     return await fetchCompanyCompletionStatus();
//   };

//   const updatePageName = (name) => {
//     sessionStorage.setItem("pageName", name);
//     setPageName(name);
//   };

//   let loginUser = async (body) => {
//     document.getElementById("loader").classList.remove("hidden");
//     try {
//     const { status, data } = await apiPost(
//       apiPath.loginUser,
//       pick(body, [
//         "email",
//         "password",
//         "deviceId",
//         "deviceType",
//         "deviceToken",
//         "latitude",
//         "longitude",
//         "loginAddress",
//         "loginIp",
//         "fraudDeviceId"
//       ]),
//     );

//     if (status === 200) {
//       if (data.success) {
//         // === MFA branch: no token yet, just a challenge to complete ===
//         if (data?.results?.mfaRequired || data?.results?.mfaSetupRequired) {
//           setMfaChallenge({
//             setup: !!data.results.mfaSetupRequired,
//             preAuthToken: data.results.preAuthToken,
//             userId: data.results.userId,
//             role: data.results.role,
//             qrCodeDataUrl: data.results.qrCodeDataUrl,
//             manualEntryKey: data.results.manualEntryKey,
//           });
//           document.getElementById("loader").classList.add("hidden");
//           return { mfaRequired: true };
//         }
//         // === END MFA branch ===

//         const token = data?.results?.token || null;
//         const refresh_token = data?.results?.refresh_token || null;

//         // Store full user data
//         const userData = data?.results;
//         const {
//           token: _token,
//           refresh_token: _refresh,
//           ...userInfo
//         } = userData;

//         sessionStorage.setItem("token", token);
//         sessionStorage.setItem("refresh_token", refresh_token);
//         sessionStorage.setItem("sidebar", "open");
//         setSidebarStatus("open");
//         sessionStorage.setItem("pageName", "Dashboard");
//         setPageName("Dashboard");

//         // Set full user data
//         setUser(userInfo);
//         sessionStorage.setItem("userData", JSON.stringify(userInfo));

//         fetchCompanyCompletionStatus()
//           .catch((error) => {
//             console.error("Failed to fetch company completion:", error);
//           });

//         navigate("/dashboard");
//         notification.success(data.message);
//       } else {
//         notification.error(
//           data?.message ||
//             "Login is not allowed from this IP. Sign in from a company-approved office/network, or ask HR to add this IP for work from home.",
//         );
//       }
//     }
//     } catch (err) {
//       const data = err?.response?.data;
//       notification.error(
//         data?.message ||
//           "Login is not allowed from this IP. Sign in from a company-approved office/network, or ask HR to add this IP for work from home.",
//       );
//     } finally {
//       const el = document.getElementById("loader");
//       if (el) el.classList.add("hidden");
//     }
//   };

//   // New: completes login after the user enters their TOTP/recovery code
//   let completeMfaLogin = async (code, { isRecoveryCode } = {}) => {
//     if (!mfaChallenge?.preAuthToken) {
//       notification.error(
//         "Your login session has expired. Please sign in again.",
//       );
//       return { verified: false };
//     }

//     document.getElementById("loader").classList.remove("hidden");
//     try {
//       let loginGps = {};
//       try {
//         loginGps = JSON.parse(sessionStorage.getItem("loginGps") || "{}") || {};
//       } catch (err) {
//         loginGps = {};
//       }
//       const body = isRecoveryCode
//         ? {
//             preAuthToken: mfaChallenge.preAuthToken,
//             recoveryCode: code,
//             role: mfaChallenge.role,
//             ...loginGps,
//           }
//         : {
//             preAuthToken: mfaChallenge.preAuthToken,
//             token: code,
//             role: mfaChallenge.role,
//             ...loginGps,
//           };

//       const { data } = await apiPost(apiPath.mfaLoginVerify, body);

//       if (data?.success) {
//         const token = data?.results?.token || null;
//         const refresh_token = data?.results?.refresh_token || null;

//         // Store the full user data from the response
//         const userData = data?.results;

//         // Remove token-related fields from userData to keep it clean
//         const {
//           token: _token,
//           refresh_token: _refresh,
//           ...userInfo
//         } = userData;

//         sessionStorage.setItem("token", token);
//         sessionStorage.setItem("refresh_token", refresh_token);
//         sessionStorage.setItem("sidebar", "open");
//         setSidebarStatus("open");
//         sessionStorage.setItem("pageName", "Dashboard");
//         setPageName("Dashboard");

//         // Set the full user data instead of just the decoded token
//         setUser(userInfo);

//         // Also store user data in sessionStorage for persistence
//         sessionStorage.setItem("userData", JSON.stringify(userInfo));

//         setMfaChallenge(null);

//         fetchCompanyCompletionStatus().catch((err) =>
//           console.error("Failed to fetch company completion:", err),
//         );

//         navigate("/dashboard");
//         notification.success(data.message || "Login successful.");
//         return { verified: true };
//       }

//       // Backend issues a fresh preAuthToken on failed attempts — keep it current
//       if (data?.data?.preAuthToken) {
//         setMfaChallenge((prev) => ({
//           ...prev,
//           preAuthToken: data.data.preAuthToken,
//         }));
//       }
//       return { verified: false, message: data?.message };
//     } catch (err) {
//       return {
//         verified: false,
//         message:
//           err?.response?.data?.message ||
//           "Verification failed. Please try again.",
//       };
//     } finally {
//       document.getElementById("loader").classList.add("hidden");
//     }
//   };

//   let completeMfaSetupLogin = async (code) => {
//     if (!mfaChallenge?.preAuthToken) {
//       notification.error(
//         "Your login session has expired. Please sign in again.",
//       );
//       return { verified: false };
//     }
//     try {
//       let loginGps = {};
//       try {
//         loginGps = JSON.parse(sessionStorage.getItem("loginGps") || "{}") || {};
//       } catch (err) {
//         loginGps = {};
//       }
//       const { data } = await apiPost(apiPath.mfaLoginSetupVerify, {
//         preAuthToken: mfaChallenge.preAuthToken,
//         token: code,
//         role: mfaChallenge.role,
//         ...loginGps,
//       });
//       if (data?.success) {
//         setMfaChallenge((prev) => ({
//           ...prev,
//           pendingLogin: data,
//           recoveryCodes: data?.results?.recoveryCodes || [],
//         }));
//         return {
//           verified: true,
//           recoveryCodes: data?.results?.recoveryCodes || [],
//         };
//       }
//       if (data?.data?.preAuthToken) {
//         setMfaChallenge((prev) => ({
//           ...prev,
//           preAuthToken: data.data.preAuthToken,
//         }));
//       }
//       return { verified: false, message: data?.message };
//     } catch (err) {
//       const payload = err?.response?.data;
//       if (payload?.data?.preAuthToken) {
//         setMfaChallenge((prev) => ({
//           ...prev,
//           preAuthToken: payload.data.preAuthToken,
//         }));
//       }
//       return {
//         verified: false,
//         message: payload?.message || "Verification failed. Please try again.",
//       };
//     }
//   };

//   let finishMfaSetupLogin = async () => {
//     const data = mfaChallenge?.pendingLogin;
//     setMfaChallenge(null);
//     if (!data?.success) return;
//     const token = data?.results?.token || null;
//     const refresh_token = data?.results?.refresh_token || null;
//     const userData = data?.results || {};
//     const {
//       token: _token,
//       refresh_token: _refresh,
//       recoveryCodes: _rc,
//       ...userInfo
//     } = userData;
//     sessionStorage.setItem("token", token);
//     sessionStorage.setItem("refresh_token", refresh_token);
//     sessionStorage.setItem("sidebar", "open");
//     setSidebarStatus("open");
//     sessionStorage.setItem("pageName", "Dashboard");
//     setPageName("Dashboard");
//     setUser(userInfo);
//     sessionStorage.setItem("userData", JSON.stringify(userInfo));
//     fetchCompanyCompletionStatus().catch((err) =>
//       console.error("Failed to fetch company completion:", err),
//     );
//     navigate("/dashboard");
//     notification.success(data.message || "Login successful.");
//   };

//   let cancelMfaChallenge = () => setMfaChallenge(null);

//   const updateProfile = async (formData, setPicture, setLogo) => {
//     try {
//       const res = await apiPut(apiPath.editProfile, formData);
//       console.log(res);
//       const token = res?.data?.results?.token || null;
//       const refresh_token = res?.data?.results?.refresh_token || null;
//       sessionStorage.setItem("token", token);
//       setLoginMessage(res?.data?.message);
//       sessionStorage.setItem("refresh_token", refresh_token);
//       setUser(jwt_decode(token));
//       const { message, success } = res.data;
//       if (res && success) {
//         notification.success(message);
//         setPicture({
//           cropperOpen: false,
//           img: null,
//           zoom: 2,
//           croppedImg: res?.data?.results?.profilePicture === "https://d2n5q7sk1gggpw.cloudfront.net/no_image.png" ? '/images/No-image-found.jpg': res?.data?.results?.profilePicture,
//         });
//         setLogo({
//           cropperOpen: false,
//           img: null,
//           zoom: 2,
//           croppedImg: res?.data?.results?.logo === "https://d2n5q7sk1gggpw.cloudfront.net/no_image.png" ? '/images/No-image-found.jpg': res?.data?.results?.logo,
//         });

//         // Refresh company completion status after profile update
//         refreshCompanyCompletionStatus();
//       }
//     } catch (err) {
//       console.log("err:", err);
//     }
//   };

//   const handleSidebar = () => {
//     let s1 = sessionStorage.getItem("sidebar");

//     sessionStorage.setItem("sidebar", s1 == "open" ? "close" : "open");
//     setSidebarStatus(s1 == "open" ? "close" : "open");
//   };

//   let logoutUser = async () => {
//     try {
//       await apiGet(apiPath.logout);
//     } catch (err) {
//       console.log("logout:", err?.message);
//     }
//     setUser(null);
//     setCompanyCompletionStatus({
//       percentage: 0,
//       missingFields: [],
//       isProfileComplete: false,
//       hrCount: 0,
//       accountantCount: 0,
//       message: "",
//     });
//     sessionStorage.removeItem("token");
//     sessionStorage.removeItem("refresh_token");
//     sessionStorage.removeItem("companyCompletionStatus");
//     navigate("/login");
//   };

//   useEffect(() => {
//     let sidebarOpen = "";
//     if (sessionStorage.getItem("sidebar")) {
//       sidebarOpen = sessionStorage.getItem("sidebar");
//     } else {
//       sessionStorage.setItem("sidebar", "open");
//       sidebarOpen = "open";
//     }
//     setSidebarStatus(sidebarOpen);

//     // Load company completion status from sessionStorage on initial load
//     const storedCompanyStatus = sessionStorage.getItem("companyCompletionStatus");
//     if (storedCompanyStatus) {
//       const parsedData = safeJsonParse(storedCompanyStatus);
//       if (parsedData) {
//         setCompanyCompletionStatus(parsedData);
//       }
//     }
//   }, []);

//   // Initialize user and fetch fresh company status if user is already logged in
//   useEffect(() => {
//     if (sessionStorage.getItem("token")) {
//       let userData = jwt_decode(sessionStorage.getItem("token"));
//       setUser(userData);

//       // Fetch fresh company completion status if user is already logged in
//       // Use setTimeout to avoid blocking the initial render
//       setTimeout(() => {
//         fetchCompanyCompletionStatus();
//       }, 1000);
//     }
//   }, []);

//   let contextData = {
//     user,
//     loginUser: loginUser,
//     logoutUser: logoutUser,
//     updateProfile: updateProfile,
//     sidebarStatus: sidebarStatus,
//     handleSidebar,
//     pageName,
//     updatePageName,
//     loginMessage: loginMessage,
//     setUser,
//     // Add company completion status to context
//     companyCompletionStatus,
//     isLoadingCompanyStatus,
//     refreshCompanyCompletionStatus,
//     fetchCompanyCompletionStatus,
//     mfaChallenge,
//     completeMfaLogin,
//     completeMfaSetupLogin,
//     finishMfaSetupLogin,
//     cancelMfaChallenge,
//   };
//   return (
//     <AuthContext.Provider value={contextData}>{children}</AuthContext.Provider>
//   );
// };
